import { useCallback, useMemo, useRef } from "react";

import { apiConfig } from "@integrations/APIIntegration";
import { NewFlags } from "@models/Organization";
import { buildQRSheetPdf, QR_SHEET_FOREGROUND_COLOR } from "@components/QontactPoints/QontactPointQRSheetPdf";
import { buildQRSheetFileName, QRSheetType } from "@utils/QRSheetFileName";

import type { QRTileState } from "./QRTile";

export interface QontactPointDetailData {
    id: string;
    name: string;
    latitude: number;
    longitude: number;
    buildingID: string;
    functionalUnitID: string;
    totalShifts: number;
    triggerDistance?: number;
}

export type QRMode = "both" | "whatsapp" | "app";
export type QRType = QRSheetType;

// Re-exported so existing imports/tests of this view model keep working;
// the underlying slug/file-name logic lives in @utils/QRSheetFileName so the
// PDF builder (a components/ module) can reuse it without importing screens/.
export { buildQRSheetFileName };
export { QR_SHEET_FOREGROUND_COLOR };

const defaultTriggerDistanceInMeters = 50;
// Export QR is rendered at a high, fixed pixel size (level "H" so the badge
// overlay in the print sheet doesn't break scanning) before being rasterized
// into the PDF.
export const qrExportSizeInPixels = 1024;

// Orgs rolled out on the Qontact app (AppAdoptionWeb, the "Adopción de la app"
// sidebar flag) also get the app check-in QR; everyone else sees WhatsApp only.
export const deriveQRMode = (newFlags?: NewFlags): QRMode => {
    return newFlags?.AppAdoptionWeb ? "both" : "whatsapp";
};

export const getVisibleQRCards = (qrMode: QRMode) => ({
    showWhatsAppQR: qrMode !== "app",
    showAppQR: qrMode !== "whatsapp",
});

export const buildWhatsAppQRValue = (qontactPointId: string): string => {
    const finalMessage = encodeURIComponent(`Hola! Estoy en ##${qontactPointId}##`);
    return `${apiConfig.waQRbaseUrl}?text=${finalMessage}`;
};

const buildGoogleMapsCoordinatesURL = (latitude: number, longitude: number) => {
    return `https://www.google.com/maps?q=${latitude},${longitude}`;
};

const svgToPngDataUrl = (svgElement: SVGElement, sizeInPixels: number): Promise<string> => {
    return new Promise((resolve, reject) => {
        const svgData = new XMLSerializer().serializeToString(svgElement);
        const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(svgBlob);

        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = sizeInPixels;
            canvas.height = sizeInPixels;
            const ctx = canvas.getContext("2d");

            if (!ctx) {
                URL.revokeObjectURL(url);
                reject(new Error("Could not create canvas context"));
                return;
            }

            ctx.drawImage(img, 0, 0, sizeInPixels, sizeInPixels);
            URL.revokeObjectURL(url);
            resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error("Could not load QR SVG for export"));
        };
        img.src = url;
    });
};

export const useQontactPointDetailModalViewModel = (
    qontactPoint: QontactPointDetailData,
    qrMode: QRMode
) => {
    const whatsAppQrRef = useRef<HTMLDivElement | null>(null);
    const appQrRef = useRef<HTMLDivElement | null>(null);
    // Hidden, level-"H" high-res QR codes used only to render the print PDF
    // (see QRTile: the visible tiles keep their current, lower-EC settings).
    const whatsAppExportQrRef = useRef<HTMLDivElement | null>(null);
    const appExportQrRef = useRef<HTMLDivElement | null>(null);
    const triggerDistanceInMeters = qontactPoint.triggerDistance ?? defaultTriggerDistanceInMeters;

    const { showWhatsAppQR, showAppQR } = getVisibleQRCards(qrMode);

    // QR payloads are computed synchronously today, so the tiles are always ready.
    // The loading/error states exist in QRTile for when payloads become async.
    const qrState: QRTileState = "ready";
    const retryQR = useCallback(() => {}, []);

    const whatsAppQRValue = useMemo(() => buildWhatsAppQRValue(qontactPoint.id), [qontactPoint.id]);

    const appQRValue = useMemo(() => apiConfig.appQRPayload(qontactPoint.id), [qontactPoint.id]);

    const googleMapsURL = useMemo(() => {
        return buildGoogleMapsCoordinatesURL(qontactPoint.latitude, qontactPoint.longitude);
    }, [qontactPoint.latitude, qontactPoint.longitude]);

    const mapMarkers = useMemo(() => {
        if (qontactPoint.latitude == null || qontactPoint.longitude == null) {
            return [];
        }

        return [
            {
                id: qontactPoint.id,
                status: "GREEN",
                latitude: qontactPoint.latitude,
                longitude: qontactPoint.longitude,
                title: qontactPoint.name,
            },
        ];
    }, [qontactPoint]);

    const mapCircles = useMemo(() => {
        if (
            qontactPoint.latitude == null ||
            qontactPoint.longitude == null ||
            triggerDistanceInMeters <= 0
        ) {
            return [];
        }

        return [
            {
                id: `${qontactPoint.id}-trigger-distance`,
                center: {
                    latitude: qontactPoint.latitude,
                    longitude: qontactPoint.longitude,
                },
                radiusInMeters: triggerDistanceInMeters,
                strokeColor: "#EC6C56",
                fillColor: "#EC6C56",
            },
        ];
    }, [qontactPoint, triggerDistanceInMeters]);

    const downloadQRSheet = useCallback(
        async (ref: React.RefObject<HTMLDivElement | null>, qrType: QRType) => {
            const svgElement = ref.current?.querySelector("svg");

            if (!svgElement) {
                return;
            }

            const qrPngDataUrl = await svgToPngDataUrl(svgElement, qrExportSizeInPixels);
            await buildQRSheetPdf({ qrType, pointName: qontactPoint.name, qrPngDataUrl });
        },
        [qontactPoint.name]
    );

    const downloadWhatsAppQR = useCallback(
        () => downloadQRSheet(whatsAppExportQrRef, "whatsapp"),
        [downloadQRSheet]
    );

    const downloadAppQR = useCallback(
        () => downloadQRSheet(appExportQrRef, "app"),
        [downloadQRSheet]
    );

    return {
        whatsAppQrRef,
        appQrRef,
        whatsAppExportQrRef,
        appExportQrRef,
        whatsAppQRValue,
        appQRValue,
        showWhatsAppQR,
        showAppQR,
        qrState,
        retryQR,
        googleMapsURL,
        mapMarkers,
        mapCircles,
        triggerDistanceInMeters,
        downloadWhatsAppQR,
        downloadAppQR,
    };
};
